import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, deleteDoc, doc, serverTimestamp, writeBatch, updateDoc } from '../../lib/auditedFirestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import Spinner from '../../components/Spinner';

// --- Sub-Component: Move Modal ---
const MoveItemModal = ({ item, folders, onClose, onMove }) => {
    const [destinationId, setDestinationId] = useState(''); // '' = Root

    // Filter out the item itself (if it's a folder) to prevent self-nesting cycles
    const validDestinations = folders.filter(f => f.id !== item.id);

    const handleConfirm = () => {
        onMove(item, destinationId || null);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                <h3 className="text-lg font-bold mb-4">Move "{item.title}"</h3>
                <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Select Destination Folder</label>
                    <select 
                        className="w-full p-2 border rounded"
                        value={destinationId}
                        onChange={(e) => setDestinationId(e.target.value)}
                    >
                        <option value="">(Root)</option>
                        {validDestinations.map(folder => (
                            <option key={folder.id} value={folder.id}>
                                📁 {folder.title}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 text-gray-700">Cancel</button>
                    <button onClick={handleConfirm} className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 font-bold">Move</button>
                </div>
            </div>
        </div>
    );
};

const LectureManager = ({ paper, onBack, setModal, onStartLive }) => { 
    const [lectures, setLectures] = useState([]);
    const [title, setTitle] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    
    // --- Folder State ---
    const [currentFolder, setCurrentFolder] = useState(null); // null = Root
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');

    // --- Move State ---
    const [movingItem, setMovingItem] = useState(null);

    // Fetch ALL lectures/folders for this paper
    useEffect(() => {
        const q = query(collection(db, "lectures"), where("paperId", "==", paper.id));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Sort: Folders first, then by date (newest first)
            data.sort((a, b) => {
                if (a.type === 'folder' && b.type !== 'folder') return -1;
                if (a.type !== 'folder' && b.type === 'folder') return 1;
                return (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0);
            });
            setLectures(data);
        });
        return () => unsubscribe();
    }, [paper.id]);

    // Filter items based on current directory for display
    const currentItems = lectures.filter(item => {
        const itemParent = item.parentId || null;
        const currentParent = currentFolder ? currentFolder.id : null;
        return itemParent === currentParent;
    });

    // Get all folders for Move Modal
    const allFolders = lectures.filter(l => l.type === 'folder');

    // --- Folder Actions ---
    const handleCreateFolder = async (e) => {
        e.preventDefault();
        if (!newFolderName.trim()) return;
        
        try {
            await addDoc(collection(db, "lectures"), {
                paperId: paper.id,
                title: newFolderName,
                type: 'folder',
                parentId: currentFolder ? currentFolder.id : null,
                createdAt: serverTimestamp()
            });
            setNewFolderName('');
            setIsCreatingFolder(false);
            setModal({ show: true, message: "Folder created successfully." });
        } catch (error) {
            setModal({ show: true, message: "Error creating folder: " + error.message });
        }
    };

    const handleNavigate = (folder) => {
        setCurrentFolder(folder);
    };

    const getBreadcrumbs = () => {
        if (!currentFolder) return <span className="font-bold text-gray-500">Root</span>;
        
        // Simple 1-level breadcrumb for now (Root / Current). 
        // For deep nesting, you'd need to traverse up the parentId tree.
        return (
            <div className="flex items-center gap-2 text-sm">
                <button onClick={() => setCurrentFolder(null)} className="text-purple-600 hover:underline">Root</button>
                <span className="text-gray-400">/</span>
                <span className="font-bold text-gray-700">{currentFolder.title}</span>
            </div>
        );
    };

    // --- File Actions ---
    const handleUpload = async (e) => {
        e.preventDefault();
        const file = e.target.file.files[0];
        if (!file || !title) {
            setModal({ show: true, message: "Please provide a title and select a file." });
            return;
        }

        setIsUploading(true);
        try {
            // Determine Type logic
            let fileType = 'image';
            if (file.type.includes('pdf')) fileType = 'pdf';
            else if (file.name.match(/\.(doc|docx)$/i)) fileType = 'word';
            else if (file.name.match(/\.(xls|xlsx)$/i)) fileType = 'excel';

            // Upload to Firebase Storage
            const storageRef = ref(storage, `lectures/${paper.id}/${Date.now()}-${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const url = await getDownloadURL(snapshot.ref);

            // Save metadata to Firestore
            await addDoc(collection(db, "lectures"), {
                paperId: paper.id,
                title: title,
                url: url,
                type: fileType,
                parentId: currentFolder ? currentFolder.id : null,
                createdAt: serverTimestamp()
            });

            setTitle('');
            e.target.reset(); 
            setModal({ show: true, message: "Material uploaded successfully!" });
        } catch (error) {
            console.error("Upload error:", error);
            setModal({ show: true, message: "Upload failed: " + error.message });
        } finally {
            setIsUploading(false);
        }
    };

    // --- Move Action ---
    const handleMoveItem = async (item, newParentId) => {
        try {
            await updateDoc(doc(db, "lectures", item.id), {
                parentId: newParentId
            });
            setModal({ show: true, message: `Moved "${item.title}" successfully.` });
            setMovingItem(null);
        } catch (error) {
            console.error("Move error:", error);
            setModal({ show: true, message: "Failed to move item." });
        }
    };

    const handleDelete = async (item) => {
        if(!window.confirm(`Are you sure you want to delete "${item.title}"? ${item.type === 'folder' ? 'All contents inside will be deleted.' : ''}`)) return;
        
        try {
            if (item.type === 'folder') {
                // Recursive delete logic
                const getDescendants = (parentId) => {
                    const children = lectures.filter(l => l.parentId === parentId);
                    let descendants = [...children];
                    children.forEach(child => {
                        if (child.type === 'folder') {
                            descendants = [...descendants, ...getDescendants(child.id)];
                        }
                    });
                    return descendants;
                };

                const itemsToDelete = [item, ...getDescendants(item.id)];
                
                // Keep each atomic deletion + audit receipt within rules access limits.
                for (let offset = 0; offset < itemsToDelete.length; offset += 8) {
                    const batch = writeBatch(db);
                    itemsToDelete.slice(offset, offset + 8).forEach(docToDelete => {
                        batch.delete(doc(db, "lectures", docToDelete.id));
                    });
                    await batch.commit();
                }

            } else {
                await deleteDoc(doc(db, "lectures", item.id));
            }
            setModal({ show: true, message: "Deleted successfully." });
        } catch (error) {
            setModal({ show: true, message: "Delete failed: " + error.message });
        }
    };

    // Helper to get icon
    const getIcon = (type) => {
        switch(type) {
            case 'folder': return '📁';
            case 'pdf': return '📄';
            case 'word': return '📝';
            case 'excel': return '📊';
            default: return '🖼️';
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            {/* Move Modal */}
            {movingItem && (
                <MoveItemModal 
                    item={movingItem} 
                    folders={allFolders} 
                    onClose={() => setMovingItem(null)} 
                    onMove={handleMoveItem} 
                />
            )}

            <div className="flex items-center gap-4 mb-6 border-b pb-4">
                <button onClick={onBack} className="text-purple-600 hover:underline font-semibold">&larr; Back to Papers</button>
                <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-800">Manage Materials: {paper.name}</h2>
                </div>
            </div>

            {/* Navigation Bar */}
            <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg border mb-6">
                <div>
                    {getBreadcrumbs()}
                </div>
                <button 
                    onClick={() => setIsCreatingFolder(!isCreatingFolder)} 
                    className="text-sm bg-white border border-gray-300 px-3 py-1 rounded shadow-sm hover:bg-gray-100 flex items-center gap-2"
                >
                    <span>📁</span> {isCreatingFolder ? 'Cancel' : 'New Folder'}
                </button>
            </div>

            {/* Create Folder Form */}
            {isCreatingFolder && (
                <form onSubmit={handleCreateFolder} className="mb-6 p-4 border border-dashed border-purple-300 rounded-lg bg-purple-50 flex gap-2 items-center animate-fade-in">
                    <input 
                        type="text" 
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        placeholder="Folder Name (e.g. Chapter 1)"
                        className="flex-1 p-2 border rounded focus:ring-2 focus:ring-purple-400 outline-none"
                        autoFocus
                    />
                    <button type="submit" className="bg-purple-600 text-white px-4 py-2 rounded font-bold hover:bg-purple-700">Create</button>
                </form>
            )}

            {/* Upload Form */}
            <form onSubmit={handleUpload} className="p-6 border rounded-lg bg-gray-50 mb-8 shadow-sm">
                <h3 className="font-bold text-lg text-gray-700 mb-4">
                    Upload to: <span className="text-purple-600">{currentFolder ? currentFolder.title : 'Root'}</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-bold text-gray-600 mb-1">Title</label>
                        <input 
                            type="text" 
                            value={title} 
                            onChange={e => setTitle(e.target.value)} 
                            placeholder="e.g. Lecture Notes" 
                            className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-400 outline-none" 
                            required 
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-600 mb-1">File</label>
                        <input 
                            name="file" 
                            type="file" 
                            accept=".pdf, .jpg, .jpeg, .png, .doc, .docx, .xls, .xlsx" 
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-purple-100 file:text-purple-700 hover:file:bg-purple-200 transition" 
                            required 
                        />
                        <p className="text-xs text-gray-400 mt-1">Supports: PDF, Image, Word, Excel</p>
                    </div>
                </div>
                <div className="mt-4 flex justify-end">
                    <button 
                        type="submit"
                        disabled={isUploading} 
                        className="bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold px-6 py-2 rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 shadow transition flex items-center gap-2"
                    >
                        {isUploading ? <Spinner /> : 'Upload Material'}
                    </button>
                </div>
            </form>

            {/* File/Folder List */}
            <h3 className="font-bold text-lg text-gray-800 mb-4">Contents</h3>
            <div className="space-y-3">
                {currentItems.length === 0 ? (
                    <p className="text-gray-500 text-center py-8 border-2 border-dashed rounded-lg">This folder is empty.</p>
                ) : (
                    currentItems.map(item => (
                        <div 
                            key={item.id} 
                            className={`flex justify-between items-center p-4 border rounded-lg transition shadow-sm ${item.type === 'folder' ? 'bg-yellow-50 hover:bg-yellow-100 cursor-pointer' : 'bg-white hover:bg-gray-50'}`}
                            onClick={() => item.type === 'folder' ? handleNavigate(item) : null}
                        >
                            <div className="flex items-center gap-4">
                                <div className={`h-10 w-10 flex items-center justify-center rounded-full text-2xl`}>
                                    {getIcon(item.type)}
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800">{item.title}</h4>
                                    <p className="text-xs text-gray-500">
                                        {item.type === 'folder' ? 'Folder' : `Uploaded: ${item.createdAt?.toDate().toLocaleDateString()}`}
                                    </p>
                                </div>
                            </div>
                            
                            <div className="flex gap-3" onClick={(e) => e.stopPropagation()}>
                                {/* Go Live Button (Only for Files) */}
                                {item.type !== 'folder' && (
                                    <button 
                                        onClick={() => onStartLive(item)} 
                                        className="px-3 py-1 bg-green-50 text-green-700 rounded border border-green-200 hover:bg-green-100 text-sm font-bold flex items-center gap-1"
                                    >
                                        <span>🔴</span> Go Live
                                    </button>
                                )}
                                
                                {/* View/Download Button (Only for Files) */}
                                {item.type !== 'folder' && (
                                    <a 
                                        href={item.url} 
                                        target="_blank" 
                                        rel="noreferrer" 
                                        className="px-3 py-1 bg-blue-50 text-blue-600 rounded border border-blue-200 hover:bg-blue-100 text-sm font-semibold"
                                    >
                                        View
                                    </a>
                                )}

                                <button 
                                    onClick={() => setMovingItem(item)} 
                                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded border border-gray-300 hover:bg-gray-200 text-sm font-semibold"
                                >
                                    Move
                                </button>

                                <button 
                                    onClick={() => handleDelete(item)} 
                                    className="px-3 py-1 bg-red-50 text-red-600 rounded border border-red-200 hover:bg-red-100 text-sm font-semibold"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
export default LectureManager;